import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button"]

  connect() {
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.boundOnDropdownClick = this.onDropdownClick.bind(this)
    this.boundUpdateDropdownPosition = this.updateDropdownPosition.bind(this)
    this.originalParent = null
    // Store dropdown reference at connect time (before any DOM manipulation)
    this.dropdownElement = this.element.querySelector('[data-activity-status-select-target="dropdown"]')

    // Attach click listener to dropdown for status selection
    this.dropdownElement.addEventListener("click", this.boundOnDropdownClick)
  }

  disconnect() {
    this.removeScrollListener()
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
    this.dropdownElement.removeEventListener("click", this.boundOnDropdownClick)
    this.returnDropdownToOriginalParent()
  }

  toggle(event) {
    event.stopPropagation()

    if (this.dropdownElement.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.portalDropdown()
    this.addScrollListener()
    this.dropdownElement.classList.remove("hidden")
    setTimeout(() => {
      document.addEventListener("click", this.boundCloseOnOutsideClick)
    }, 10)
  }

  close() {
    this.dropdownElement.classList.add("hidden")
    this.removeScrollListener()
    this.returnDropdownToOriginalParent()
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
  }

  addScrollListener() {
    window.addEventListener("scroll", this.boundUpdateDropdownPosition, true)
  }

  removeScrollListener() {
    window.removeEventListener("scroll", this.boundUpdateDropdownPosition, true)
  }

  updateDropdownPosition() {
    if (this.dropdownElement.classList.contains("hidden")) {
      this.removeScrollListener()
      return
    }

    const buttonRect = this.buttonTarget.getBoundingClientRect()
    this.dropdownElement.style.top = `${buttonRect.bottom + 4}px`
    this.dropdownElement.style.left = `${buttonRect.left}px`
  }

  closeOnOutsideClick(event) {
    // Check if click is outside both the original element (button) and the dropdown
    const isClickOutsideElement = !this.element.contains(event.target)
    const isClickOutsideDropdown = !this.dropdownElement.contains(event.target)

    if (isClickOutsideElement && isClickOutsideDropdown) {
      this.close()
    }
  }

  portalDropdown() {
    // Store original parent and insertBefore reference
    if (!this.originalParent) {
      this.originalParent = {
        nextSibling: this.dropdownElement.nextElementSibling
      }
    }

    // Get button's position on screen
    const buttonRect = this.buttonTarget.getBoundingClientRect()

    // Position dropdown at the button's bottom-right corner using fixed positioning
    this.dropdownElement.style.position = "fixed"
    this.dropdownElement.style.top = `${buttonRect.bottom + 4}px`
    this.dropdownElement.style.left = `${buttonRect.left}px`
    this.dropdownElement.style.right = "auto"

    // Move dropdown to body to escape overflow: hidden containers
    document.body.appendChild(this.dropdownElement)
  }

  returnDropdownToOriginalParent() {
    if (this.originalParent && this.element) {
      // Reset inline styles
      this.dropdownElement.style.position = ""
      this.dropdownElement.style.top = ""
      this.dropdownElement.style.right = ""
      this.dropdownElement.style.left = ""

      // Return to original position (at end of this.element)
      this.element.appendChild(this.dropdownElement)

      this.originalParent = null
    }
  }

  onDropdownClick(event) {
    // Find the button with data-status attribute (status option buttons)
    const statusButton = event.target.closest('[data-status]')
    if (statusButton) {
      event.stopPropagation()
      const selectedStatus = statusButton.dataset.status
      const previousHTML = this.buttonTarget.innerHTML
      const previousClasses = this.buttonTarget.className
      const selectedItems = Array.from(this.dropdownElement.querySelectorAll('[data-status]'), button => [button.parentElement, button.parentElement.className])
      const rollback = () => {
        this.buttonTarget.innerHTML = previousHTML
        this.buttonTarget.className = previousClasses
        selectedItems.forEach(([element, classes]) => { element.className = classes })
      }

      // Update main button innerHTML completely
      this.buttonTarget.innerHTML = statusButton.innerHTML

      // Update button styling (remove w-full class that's specific to dropdown items)
      const buttonClasses = statusButton.className
      this.buttonTarget.className = buttonClasses.replace('w-full ', '')

      // Update selected state in dropdown
      this.dropdownElement.querySelectorAll('[data-status]').forEach(btn => {
        const parent = btn.parentElement
        if (btn.dataset.status === selectedStatus) {
          parent.classList.add('bg-[#EBEBFF]')
        } else {
          parent.classList.remove('bg-[#EBEBFF]')
        }
      })

      // Dispatch custom event for parent components to handle
      this.element.dispatchEvent(new CustomEvent("status-changed", {
        detail: { status: selectedStatus, rollback },
        bubbles: true
      }))

      this.close()
    }
  }
}
